import React, { useLayoutEffect, useRef, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import Footer from './components/Footer';
import ImpersonationBanner from './components/ImpersonationBanner';
import ZihinRehberiButton from './components/ZihinRehberiButton';
import ScrollToTop from './components/ScrollToTop';
import { ToastProvider } from './components/ui/toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ZihinKilavuzuProvider } from './contexts/ZihinKilavuzuContext';
import { ZihinKilavuzuTooltip, ZihinKilavuzuTrigger } from './components/zihinKilavuzu';
import { RHIEProvider } from './components/rhie';
import { MindCareProvider } from './components/mindcare';
import { CosmicNotificationProvider } from './contexts/CosmicNotificationContext';
import { startAnimatedFavicon, stopAnimatedFavicon } from './utils/animatedFavicon';

// Eagerly loaded pages (critical path)
import HomePage from './pages/HomePage';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import OAuthCallback from './pages/OAuthCallback';
import PanelGate from './pages/PanelGate';

// Retry wrapper for lazy loading (handles stale chunks after deploy)
function lazyRetry(fn) {
  return lazy(() => fn().catch(() => {
    const reloaded = sessionStorage.getItem('chunk_reload');
    if (!reloaded) {
      sessionStorage.setItem('chunk_reload', '1');
      window.location.reload();
    }
    return fn();
  }));
}

// Lazy loaded pages for code splitting
const TrainingsPage = lazyRetry(() => import('./pages/TrainingsPage'));
const TrainingDetail = lazyRetry(() => import('./pages/TrainingDetail'));
const ExpertiseDetail = lazyRetry(() => import('./pages/ExpertiseDetail'));
const BlogPage = lazyRetry(() => import('./pages/BlogPage'));
const BlogPost = lazyRetry(() => import('./pages/BlogPost'));
const VIPMembership = lazyRetry(() => import('./pages/VIPMembership'));
const PrivacyPolicy = lazyRetry(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazyRetry(() => import('./pages/TermsOfService'));
const LegalDisclaimer = lazyRetry(() => import('./pages/LegalDisclaimer'));

// Panel Pages (Lazy)
const PanelDashboard = lazyRetry(() => import('./pages/panel/Dashboard'));
const PanelDashboardNew = lazyRetry(() => import('./pages/panel/DashboardNew')); // Auto Guidance Engine
const RitualRunnerPage = lazyRetry(() => import('./pages/panel/RitualRunnerPage')); // Ritual Runner v1
const RitualEntry = lazyRetry(() => import('./pages/panel/RitualEntry')); // FAZ 7: Post-Login Ritual Entry
const DailyClosure = lazyRetry(() => import('./pages/panel/DailyClosure')); // FAZ 7.2: Kapanış Telkini

// Masterclass Pages (Lazy)
const MasterclassLanding = lazyRetry(() => import('./pages/masterclass/MasterclassLanding'));
const MasterclassRegister = lazyRetry(() => import('./pages/masterclass/MasterclassRegister'));
const FunnelThankYou = lazyRetry(() => import('./pages/masterclass/FunnelThankYou'));
const FunnelGorusme = lazyRetry(() => import('./pages/masterclass/FunnelGorusme'));
const FunnelKabul = lazyRetry(() => import('./pages/masterclass/FunnelKabul'));
const FunnelRed = lazyRetry(() => import('./pages/masterclass/FunnelRed'));
const FunnelDurumTakip = lazyRetry(() => import('./pages/masterclass/FunnelDurumTakip'));
const FunnelDavetiye = lazyRetry(() => import('./pages/masterclass/FunnelDavetiye'));
const PanelProfile = lazyRetry(() => import('./pages/panel/Profile'));
const PanelMyTrainings = lazyRetry(() => import('./pages/panel/MyTrainings'));
const PanelTrainingView = lazyRetry(() => import('./pages/panel/TrainingView'));
const PanelMyCertificates = lazyRetry(() => import('./pages/panel/MyCertificates'));
const PanelMindJourney = lazyRetry(() => import('./pages/panel/MindJourney'));
const PanelMindPoints = lazyRetry(() => import('./pages/panel/MindPoints'));
const VIPPanel = lazyRetry(() => import('./pages/panel/VIPPanel'));
const VIPNetworking = lazyRetry(() => import('./pages/panel/VIPNetworking'));
const PartnerAdvantages = lazyRetry(() => import('./pages/panel/PartnerAdvantages'));
const PartnerDashboard = lazyRetry(() => import('./pages/panel/PartnerDashboard'));
const PartnerDashboardNew = lazyRetry(() => import('./pages/panel/partner/PartnerDashboardNew'));
const PartnerRegister = lazyRetry(() => import('./pages/PartnerRegister'));
const PanelPayments = lazyRetry(() => import('./pages/panel/MyOrders'));
const PanelSupport = lazyRetry(() => import('./pages/panel/Support'));
const PanelMyZoomEvents = lazyRetry(() => import('./pages/panel/MyZoomEvents'));
const VideoTraining = lazyRetry(() => import('./pages/panel/VideoTraining'));
const ReferralDashboard = lazyRetry(() => import('./pages/panel/ReferralDashboard'));
const AudioPlayerPage = lazyRetry(() => import('./pages/panel/AudioPlayerPage'));
const RewardStorePage = lazyRetry(() => import('./components/gamification/RewardStore/RewardStorePage'));

// Admin Pages (Lazy)
const AdminDashboard = lazyRetry(() => import('./pages/admin/Dashboard'));
const AdminUsers = lazyRetry(() => import('./pages/admin/Users'));
const AdminTrainings = lazyRetry(() => import('./pages/admin/Trainings'));
const AdminSettings = lazyRetry(() => import('./pages/admin/Settings'));
const AdminVIPManagement = lazyRetry(() => import('./pages/admin/VIPManagement'));
const AdminCertificates = lazyRetry(() => import('./pages/admin/Certificates'));
const CertificateVerify = lazyRetry(() => import('./pages/CertificateVerify'));
const AdminReferrals = lazyRetry(() => import('./pages/admin/Referrals'));
const AdminDiscounts = lazyRetry(() => import('./pages/admin/Discounts'));
const AdminDiscountCodes = lazyRetry(() => import('./pages/admin/DiscountCodes'));
const AdminMentors = lazyRetry(() => import('./pages/admin/Mentors'));
const AdminOrders = lazyRetry(() => import('./pages/admin/Orders'));
const AdminOrderManagement = lazyRetry(() => import('./pages/admin/OrderManagement'));
const AdminRegionalLeaders = lazyRetry(() => import('./pages/admin/RegionalLeaders'));
const RoleApplications = lazyRetry(() => import('./pages/admin/RoleApplications'));
const AdminZoomEvents = lazyRetry(() => import('./pages/admin/ZoomEvents'));
const AdminRoleManagement = lazyRetry(() => import('./pages/admin/RoleManagement'));
const AdminPartnerManagement = lazyRetry(() => import('./pages/admin/PartnerManagement'));
const AdminRevenueManagement = lazyRetry(() => import('./pages/admin/RevenueManagement'));
const AdminAnalytics = lazyRetry(() => import('./pages/admin/AdminAnalytics'));
const AdminNotificationManagement = lazyRetry(() => import('./pages/admin/NotificationManagement'));
const AdminMindCare = lazyRetry(() => import('./pages/admin/AdminMindCare'));
const AdminAudio = lazyRetry(() => import('./pages/admin/AdminAudio'));
const AdminBehavioralDashboard = lazyRetry(() => import('./pages/admin/BehavioralDashboard'));
const AdminFunnelManagement = lazyRetry(() => import('./pages/admin/FunnelManagement'));
const ZihinRehberiAnalytics = lazyRetry(() => import('./pages/admin/ZihinRehberiAnalytics'));
const AdminJourneyManagement = lazyRetry(() => import('./pages/admin/JourneyManagement'));
const AdminRevenueReport = lazyRetry(() => import('./pages/admin/RevenueReport'));
const AdminBlogManagement = lazyRetry(() => import('./pages/admin/BlogManagement'));
const AdminBlogAIEngine = lazyRetry(() => import('./pages/admin/BlogAIEngine'));
const AdminContentEngine = lazyRetry(() => import('./pages/admin/ContentEngine'));
const AdminBlogEditor = lazyRetry(() => import('./pages/admin/BlogEditor'));
const AdminEmailManagement = lazyRetry(() => import('./pages/admin/EmailManagement'));

// Mentor Pages (Lazy)
const MentorDashboard = lazyRetry(() => import('./pages/mentor/MentorDashboard'));
const MentorApplication = lazyRetry(() => import('./pages/mentor/MentorApplication'));
const MentorLeaderboard = lazyRetry(() => import('./pages/mentor/MentorLeaderboard'));
const PublicMentorProfile = lazyRetry(() => import('./pages/mentor/PublicMentorProfile'));
const RegionalLeaderDashboard = lazyRetry(() => import('./pages/mentor/RegionalLeaderDashboard'));
const MasterDashboard = lazyRetry(() => import('./pages/mentor/MasterDashboard'));

// Loading Spinner Component
function PageLoader() {
  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-white/50 text-sm">Yükleniyor...</p>
      </div>
    </div>
  );
}

// Scroll to top on route change - using useLayoutEffect for synchronous scroll
function ScrollToTopOnNavigate() {
  const { pathname } = useLocation();
  const prevPathRef = useRef(pathname);

  useLayoutEffect(() => {
    if (prevPathRef.current !== pathname) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      prevPathRef.current = pathname;
    }
  }, [pathname]);

  return null;
}

// Protected Route Component - P0 Fix: More robust auth check
function ProtectedRoute({ children, adminOnly = false, requiredTier = null }) {
  const { user, loading, authInitialized, isAdmin, isAuthenticated, hasPanelAccess, accessTier } = useAuth();
  const location = useLocation();

  // P0 Fix: Wait for auth to be fully initialized before making decisions
  if (loading || !authInitialized) {
    return <PageLoader />;
  }

  // P0 Fix: Check both user AND token via isAuthenticated
  if (!isAuthenticated) {
    console.log('ProtectedRoute: Not authenticated, redirecting to /giris');
    return <Navigate to="/giris" state={{ from: location }} replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/panel" replace />;
  }

  // Panel access gate: users without purchases go to gate page
  if (!hasPanelAccess && location.pathname.startsWith('/panel')) {
    return <Navigate to="/panel-erisim" replace />;
  }

  // Tier-based feature gating: show locked view if tier insufficient
  if (requiredTier && !isAdmin) {
    const tierLevel = { none: 0, masterclass: 1, full: 2, admin: 3 };
    const userLevel = tierLevel[accessTier] || 0;
    const requiredLevel = tierLevel[requiredTier] || 0;
    if (userLevel < requiredLevel) {
      return (
        <Suspense fallback={<PageLoader />}>
          <LockedPageView requiredTier={requiredTier} />
        </Suspense>
      );
    }
  }

  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

// Locked page view for insufficient tier
function LockedPageView({ requiredTier }) {
  return (
    <div className="min-h-screen bg-[#06070D] flex items-center justify-center px-4">
      <div className="max-w-md text-center">
        <div className="w-16 h-16 rounded-2xl bg-violet-500/15 flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-white mb-3">
          {requiredTier === 'full' ? 'Premium Erisim Gerekli' : 'Masterclass Erisimi Gerekli'}
        </h1>
        <p className="text-base text-white/50 mb-6">
          {requiredTier === 'full'
            ? 'Bu bolume erismek icin Ana Egitim programina kayit olmaniz gerekiyor. Egitim sistemi, sertifikalar ve network agina tam erisim kazanin.'
            : 'Bu bolume erismek icin Masterclass\'a katilmaniz gerekiyor.'}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href={requiredTier === 'full' ? '/panel-erisim' : '/masterclass'}
            className="px-6 py-3 bg-violet-600 hover:bg-violet-500 text-white font-medium rounded-xl transition-colors"
            data-testid="locked-page-upgrade-btn"
          >
            {requiredTier === 'full' ? 'Tam Erisim Hakkinda' : 'Masterclass\'a Katil'}
          </a>
          <a
            href="/panel"
            className="px-6 py-3 bg-white/5 hover:bg-white/10 text-white/70 font-medium rounded-xl border border-white/10 transition-colors"
          >
            Dashboard'a Don
          </a>
        </div>
      </div>
    </div>
  );
}

// Public Layout (with Header/Footer)
function PublicLayout({ children }) {
  return (
    <>
      <Header />
      <Suspense fallback={<PageLoader />}>{children}</Suspense>
      <Footer />
      <ZihinRehberiButton />
      <ScrollToTop />
    </>
  );
}

function AppRoutes() {
  return (
    <div className="App min-h-screen bg-black text-white">
      <ScrollToTopOnNavigate />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
        <Route path="/egitimler" element={<PublicLayout><TrainingsPage /></PublicLayout>} />
        <Route path="/egitim/:id" element={<PublicLayout><TrainingDetail /></PublicLayout>} />
        <Route path="/uzmanlik/:slug" element={<PublicLayout><ExpertiseDetail /></PublicLayout>} />
        <Route path="/vip" element={<Suspense fallback={<PageLoader />}><VIPMembership /></Suspense>} />
        
        {/* Funnel Routes (Public - with Header/Footer for brand consistency) */}
        <Route path="/masterclass" element={<PublicLayout><MasterclassLanding /></PublicLayout>} />
        <Route path="/masterclass/basvuru" element={<PublicLayout><MasterclassRegister /></PublicLayout>} />
        <Route path="/masterclass/tesekkurler" element={<PublicLayout><FunnelThankYou /></PublicLayout>} />
        <Route path="/masterclass/durum" element={<PublicLayout><FunnelDurumTakip /></PublicLayout>} />
        <Route path="/masterclass/gorusme" element={<PublicLayout><FunnelGorusme /></PublicLayout>} />
        <Route path="/masterclass/davetiye" element={<PublicLayout><FunnelDavetiye /></PublicLayout>} />
        <Route path="/masterclass/kabul" element={<PublicLayout><FunnelKabul /></PublicLayout>} />
        <Route path="/masterclass/kabul-degil" element={<PublicLayout><FunnelRed /></PublicLayout>} />
        {/* Legacy redirects */}
        <Route path="/gorusme" element={<Navigate to="/masterclass/gorusme" replace />} />
        <Route path="/davetiye" element={<Navigate to="/masterclass/davetiye" replace />} />
        <Route path="/kabul" element={<Navigate to="/masterclass/kabul" replace />} />
        <Route path="/kabul-degil" element={<Navigate to="/masterclass/kabul-degil" replace />} />
        
        {/* Blog Routes */}
        <Route path="/blog" element={<PublicLayout><BlogPage /></PublicLayout>} />
        <Route path="/blog/:slug" element={<PublicLayout><BlogPost /></PublicLayout>} />

        {/* Legal Pages */}
        <Route path="/gizlilik" element={<PublicLayout><PrivacyPolicy /></PublicLayout>} />
        <Route path="/yasal-uyari" element={<PublicLayout><LegalDisclaimer /></PublicLayout>} />
        <Route path="/kullanim-kosullari" element={<PublicLayout><TermsOfService /></PublicLayout>} />
        
        {/* Auth Routes */}
        <Route path="/giris" element={<Login />} />
        <Route path="/kayit" element={<Register />} />
        <Route path="/auth/callback" element={<OAuthCallback />} />
        
        {/* Panel Access Gate - for users without purchases */}
        <Route path="/panel-erisim" element={<PanelGate />} />
        
        {/* Panel Routes (Protected) */}
        <Route path="/panel/entry" element={<ProtectedRoute><RitualEntry /></ProtectedRoute>} />
        <Route path="/panel/closure" element={<ProtectedRoute><DailyClosure /></ProtectedRoute>} />
        <Route path="/panel" element={<ProtectedRoute><PanelDashboard /></ProtectedRoute>} />
        <Route path="/panel/focus" element={<ProtectedRoute><PanelDashboardNew /></ProtectedRoute>} />
        <Route path="/panel/ritual" element={<ProtectedRoute><RitualRunnerPage /></ProtectedRoute>} />
        <Route path="/panel/egitimlerim" element={<ProtectedRoute requiredTier="full"><PanelMyTrainings /></ProtectedRoute>} />
        <Route path="/panel/egitim/:trainingId" element={<ProtectedRoute requiredTier="full"><PanelTrainingView /></ProtectedRoute>} />
        <Route path="/panel/video-egitim/:trainingId" element={<ProtectedRoute requiredTier="full"><VideoTraining /></ProtectedRoute>} />
        <Route path="/panel/sertifikalarim" element={<ProtectedRoute requiredTier="full"><PanelMyCertificates /></ProtectedRoute>} />
        <Route path="/panel/zihin-yolculugum" element={<ProtectedRoute><PanelMindJourney /></ProtectedRoute>} />
        <Route path="/panel/zihin-puanlarim" element={<ProtectedRoute><PanelMindPoints /></ProtectedRoute>} />
        <Route path="/panel/zihin-zirvesi" element={<ProtectedRoute requiredTier="full"><VIPPanel /></ProtectedRoute>} />
        <Route path="/panel/vip-network" element={<ProtectedRoute requiredTier="full"><VIPNetworking /></ProtectedRoute>} />
        <Route path="/panel/partner-avantaj" element={<ProtectedRoute><PartnerAdvantages /></ProtectedRoute>} />
        <Route path="/panel/partner-dashboard" element={<ProtectedRoute><PartnerDashboard /></ProtectedRoute>} />
        <Route path="/panel/partner-dashboard-new" element={<ProtectedRoute><PartnerDashboardNew /></ProtectedRoute>} />
        <Route path="/panel/partner-dashboard-new/company" element={<ProtectedRoute><PartnerDashboardNew /></ProtectedRoute>} />
        <Route path="/panel/partner-dashboard-new/advantages" element={<ProtectedRoute><PartnerDashboardNew /></ProtectedRoute>} />
        <Route path="/panel/partner-dashboard-new/qr" element={<ProtectedRoute><PartnerDashboardNew /></ProtectedRoute>} />
        <Route path="/panel/partner-dashboard-new/approvals" element={<ProtectedRoute><PartnerDashboardNew /></ProtectedRoute>} />
        <Route path="/panel/partner-dashboard-new/analytics" element={<ProtectedRoute><PartnerDashboardNew /></ProtectedRoute>} />
        <Route path="/panel/partner-dashboard-new/billing" element={<ProtectedRoute><PartnerDashboardNew /></ProtectedRoute>} />
        <Route path="/panel/partner-dashboard-new/sponsorship" element={<ProtectedRoute><PartnerDashboardNew /></ProtectedRoute>} />
        <Route path="/panel/partner-dashboard-new/support" element={<ProtectedRoute><PartnerDashboardNew /></ProtectedRoute>} />
        <Route path="/partner/basvuru" element={<Suspense fallback={<PageLoader />}><PartnerRegister /></Suspense>} />
        <Route path="/panel/odemelerim" element={<ProtectedRoute><PanelPayments /></ProtectedRoute>} />
        <Route path="/panel/profil" element={<ProtectedRoute><PanelProfile /></ProtectedRoute>} />
        <Route path="/panel/destek" element={<ProtectedRoute><PanelSupport /></ProtectedRoute>} />
        <Route path="/panel/zoom" element={<ProtectedRoute requiredTier="full"><PanelMyZoomEvents /></ProtectedRoute>} />
        <Route path="/panel/referans" element={<ProtectedRoute><ReferralDashboard /></ProtectedRoute>} />
        <Route path="/panel/telkinler" element={<ProtectedRoute><AudioPlayerPage /></ProtectedRoute>} />
        <Route path="/panel/odul-magazasi" element={<ProtectedRoute><RewardStorePage /></ProtectedRoute>} />
        
        {/* Admin Routes (Protected + Admin Only) */}
        <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/kullanicilar" element={<ProtectedRoute adminOnly><AdminUsers /></ProtectedRoute>} />
        <Route path="/admin/egitimler" element={<ProtectedRoute adminOnly><AdminTrainings /></ProtectedRoute>} />
        <Route path="/admin/referanslar" element={<ProtectedRoute adminOnly><AdminReferrals /></ProtectedRoute>} />
        <Route path="/admin/indirimler" element={<ProtectedRoute adminOnly><AdminDiscounts /></ProtectedRoute>} />
        <Route path="/admin/indirim-kodlari" element={<ProtectedRoute adminOnly><AdminDiscountCodes /></ProtectedRoute>} />
        <Route path="/admin/mentorler" element={<ProtectedRoute adminOnly><AdminMentors /></ProtectedRoute>} />
        <Route path="/admin/siparisler" element={<ProtectedRoute adminOnly><AdminOrders /></ProtectedRoute>} />
        <Route path="/admin/siparis-yonetimi" element={<ProtectedRoute adminOnly><AdminOrderManagement /></ProtectedRoute>} />
        <Route path="/admin/bolge-liderleri" element={<ProtectedRoute adminOnly><AdminRegionalLeaders /></ProtectedRoute>} />
        <Route path="/admin/basvurular" element={<ProtectedRoute adminOnly><RoleApplications /></ProtectedRoute>} />
        <Route path="/admin/ayarlar" element={<ProtectedRoute adminOnly><AdminSettings /></ProtectedRoute>} />
        <Route path="/admin/vip" element={<ProtectedRoute adminOnly><AdminVIPManagement /></ProtectedRoute>} />
        <Route path="/admin/sertifikalar" element={<ProtectedRoute adminOnly><AdminCertificates /></ProtectedRoute>} />
        <Route path="/admin/zoom" element={<ProtectedRoute adminOnly><AdminZoomEvents /></ProtectedRoute>} />
        <Route path="/admin/rol-yonetimi" element={<ProtectedRoute adminOnly><AdminRoleManagement /></ProtectedRoute>} />
        <Route path="/admin/partnerler" element={<ProtectedRoute adminOnly><AdminPartnerManagement /></ProtectedRoute>} />
        <Route path="/admin/gelir" element={<ProtectedRoute adminOnly><AdminRevenueManagement /></ProtectedRoute>} />
        <Route path="/admin/bildirimler" element={<ProtectedRoute adminOnly><AdminNotificationManagement /></ProtectedRoute>} />
        <Route path="/admin/analytics" element={<ProtectedRoute adminOnly><AdminAnalytics /></ProtectedRoute>} />
        <Route path="/admin/mindcare" element={<ProtectedRoute adminOnly><AdminMindCare /></ProtectedRoute>} />
        <Route path="/admin/audio" element={<ProtectedRoute adminOnly><AdminAudio /></ProtectedRoute>} />
        <Route path="/admin/behavior" element={<ProtectedRoute adminOnly><AdminBehavioralDashboard /></ProtectedRoute>} />
        <Route path="/admin/masterclass" element={<ProtectedRoute adminOnly><AdminFunnelManagement /></ProtectedRoute>} />
        <Route path="/admin/zihin-rehberi" element={<ProtectedRoute adminOnly><ZihinRehberiAnalytics /></ProtectedRoute>} />
        <Route path="/admin/yolculuklar" element={<ProtectedRoute adminOnly><AdminJourneyManagement /></ProtectedRoute>} />
        <Route path="/admin/gelir-raporu" element={<ProtectedRoute adminOnly><AdminRevenueReport /></ProtectedRoute>} />
        <Route path="/admin/blog" element={<ProtectedRoute adminOnly><AdminBlogManagement /></ProtectedRoute>} />
        <Route path="/admin/blog-ai" element={<ProtectedRoute adminOnly><AdminBlogAIEngine /></ProtectedRoute>} />
        <Route path="/admin/icerik-studyosu" element={<ProtectedRoute adminOnly><AdminContentEngine /></ProtectedRoute>} />
        <Route path="/admin/blog-editor/:id" element={<ProtectedRoute adminOnly><AdminBlogEditor /></ProtectedRoute>} />
        <Route path="/admin/email" element={<ProtectedRoute adminOnly><AdminEmailManagement /></ProtectedRoute>} />
        <Route path="/sertifika/dogrula/:code" element={<Suspense fallback={<PageLoader />}><CertificateVerify /></Suspense>} />

        {/* Mentor Routes */}
        <Route path="/mentor" element={<ProtectedRoute><MentorDashboard /></ProtectedRoute>} />
        <Route path="/mentor-basvuru" element={<Suspense fallback={<PageLoader />}><MentorApplication /></Suspense>} />
        <Route path="/mentor/liderlik" element={<Suspense fallback={<PageLoader />}><MentorLeaderboard /></Suspense>} />
        <Route path="/mentor/bolge" element={<ProtectedRoute><RegionalLeaderDashboard /></ProtectedRoute>} />
        <Route path="/mentor/usta" element={<ProtectedRoute><MasterDashboard /></ProtectedRoute>} />
        <Route path="/mentor/:mentorCode" element={<Suspense fallback={<PageLoader />}><PublicMentorProfile /></Suspense>} />

        {/* 404 Catch-All */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

// Conditionally show ZihinKilavuzu only on panel/admin routes
function ConditionalZihinKilavuzu() {
  const location = useLocation();
  const isPanel = location.pathname.startsWith('/panel') || location.pathname.startsWith('/admin');
  if (!isPanel) return null;
  return <ZihinKilavuzuTrigger />;
}

function App() {
  useEffect(() => {
    startAnimatedFavicon();
    return () => stopAnimatedFavicon();
  }, []);

  return (
    <AuthProvider>
      <CosmicNotificationProvider>
        <ToastProvider>
          <RHIEProvider>
            <MindCareProvider>
              <ZihinKilavuzuProvider>
                <ImpersonationBanner />
                <AppRoutes />
                <ZihinKilavuzuTooltip />
                <ConditionalZihinKilavuzu />
              </ZihinKilavuzuProvider>
            </MindCareProvider>
          </RHIEProvider>
        </ToastProvider>
      </CosmicNotificationProvider>
    </AuthProvider>
  );
}

export default App;
